import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { audiencesApi } from '../../../../api/audiences';
import {
  clearState,
  createdSegment,
  getSegmentId,
  getUserSegment,
  getUserSegmentName,
  onRequest,
  setSegmentName,
} from '../../../../store/reducers/segment';
import { getUser } from '../../../../store/reducers/user';
import { trackAudienceCreated } from '../../../../utils/mixpanel/mixpanelEvents';
import uniqueId from '../../../../utils/uniqueId';
import { showErrorMessage } from '../../../base/Notifications';
import NameModal from '../../../ui/modals/NameModal';
import Tooltip from '../../../ui/Tooltip';

const SegmentsFooter = ({ folder, disabled, setTitle }) => {
  const dispatch = useDispatch();
  const userSegment = useSelector(getUserSegment);
  const segmentId = useSelector(getSegmentId);
  const userSegmentName = useSelector(getUserSegmentName);
  const [createUserSegment, createUserSegmentResult] =
    audiencesApi.useCreateSegmentMutation();
  const [changeUserSegment, changeUserSegmentResult] =
    audiencesApi.useChangeSegmentMutation();
  const navigate = useNavigate();
  const user = useSelector(getUser);
  const [showNameModal, setShowNameModal] = useState(false);

  const disabledTooltipId = uniqueId('disabled-segment');

  const createSegment = async () => {
    let segmentToPush;
    if (folder) {
      segmentToPush = {
        ...userSegment,
        name: userSegmentName,
        folders: [folder],
      };
    } else {
      segmentToPush = { ...userSegment, name: userSegmentName };
    }
    if (!segmentToPush.name) {
      setShowNameModal(true);
      return;
    }
    try {
      if (!segmentId) {
        const result = await createUserSegment(segmentToPush);
        if (result.error) {
          dispatch(onRequest(false));
          showErrorMessage(result.error.data.error);
          return;
        }
        dispatch(clearState());
        dispatch(onRequest(false));
        if (result?.data) {
          dispatch(createdSegment(result?.data?.id));
        }

        navigate('/audiences');
      } else if (segmentId) {
        const result = await changeUserSegment({
          id: segmentId,
          ...segmentToPush,
        });
        if (result.error) {
          dispatch(onRequest(false));
          showErrorMessage(result.error.data.message);
          return;
        }
        dispatch(clearState());
        dispatch(onRequest(false));
        if (result?.data?.status === 200) {
          dispatch(createdSegment(segmentId));
        }

        navigate('/audiences');
      }
    } catch (err) {
      showErrorMessage(err.message);
    }
  };

  useEffect(() => {
    if (
      createUserSegmentResult.data?.user &&
      createUserSegmentResult.status === 'fulfilled'
    ) {
      trackAudienceCreated(user, createUserSegmentResult.data);
    }
    if (
      changeUserSegmentResult.isLoading ||
      changeUserSegmentResult.isFetching ||
      createUserSegmentResult.isLoading ||
      createUserSegmentResult.isFetching
    ) {
      dispatch(onRequest(true));
    } else {
      dispatch(onRequest(false));
    }
  }, [createUserSegmentResult, changeUserSegmentResult, dispatch]);

  const footerStyle = {
    left: '0px',
    marginTop: '100px',
    zIndex: 8,
  };

  const onNameCancel = () => {
    setTitle('');
    dispatch(setSegmentName(''));
    setShowNameModal(false);
  };

  const onNameChange = (name) => {
    setTitle(name);
    dispatch(setSegmentName(name));
  };

  const onNameSave = () => {
    createSegment();
    setShowNameModal(false);
  };

  return (
    <>
      <footer
        style={{ ...footerStyle }}
        className="wrapper-footer d-flex align-items-center justify-content-end gap-2"
      >
        <button
          className="outline-button"
          type="button"
          onClick={() => {
            navigate('/audiences');
          }}
        >
          Cancel
        </button>
        <button
          className="regular-button"
          type="button"
          onClick={createSegment}
          disabled={disabled}
          data-for={disabledTooltipId}
          data-tip
        >
          {segmentId ? 'Confirm changes' : 'Save segment'}
        </button>
        {disabled ? (
          <Tooltip
            id={disabledTooltipId}
            info="Your segment cannot be saved as it is invalid"
          />
        ) : null}
      </footer>
      {showNameModal && (
        <NameModal
          title="Name your segment"
          description="To save a segment you will need to give it a name"
          placeholder="Segment Name"
          onCancel={onNameCancel}
          onSave={onNameSave}
          onChange={onNameChange}
        />
      )}
    </>
  );
};

export default SegmentsFooter;
